import { CommonBookingCalendar } from 'common/components/entities/Calendar'
import {
  BookingCalendarSteps,
  EventTimeZoneTypeEnum,
} from 'common/components/entities/Calendar/constants'
import { BookingCalendarInterface } from 'common/types/entities/BookingCalendarInterface'
import ChildrenEntities from 'publisher/components/ChildrenEntities'
import StyleWrapper from 'publisher/components/core/StyleWrapper'
import { useClosePopup } from 'publisher/hooks/use-close-popup'
import useBlog, { selectors as blogSelectors } from 'publisher/hooks/useBlog'
import { managementSelectors, useManagement } from 'publisher/store'
import { QuestionAnswer } from 'publisher/store/optIn/optInStateInterface'
import React, { useRef, useState } from 'react'
import { scrollElementTopIntoViewIfTopIntoView } from 'src/publisher/utils/scroll-element-top-into-view-if-top-into-view'
import { useBookingCalendarConfirmed } from './use-booking-calendar-confirmed'
import { useBookingCalendarOptIn } from './use-booking-calendar-opt-in'
import { useBookingCalendarParams } from './use-booking-calendar-params'
import { useCalendarErrors } from './use-calendar-step-errors'

interface BookingCalendarProps {
  entity: BookingCalendarInterface
}

function BookingCalendar({ entity }: BookingCalendarProps) {
  const calendarRef = useRef<HTMLDivElement>(null)
  const {
    event,
    timeSlots,
    isEventFetching,
    activeStep,
    setActiveStep,
    selectedDate,
    selectedTimeZone,
    selectedTimeSlot,
    selectedLocationId,
    handleChangeField,
    onMonthSelect,
    globalFontFamily,
    globalMobileFontSize,
    globalFontSize,
  } = useBookingCalendarParams({ entity })
  const isDesktop = useManagement(managementSelectors.isDesktop)
  const [customQuestions, setCustomQuestions] = useState<QuestionAnswer[]>([])

  const blogLayoutPageId = useBlog(blogSelectors.getBlogLayoutPageId)
  const closePopup = useClosePopup(entity, blogLayoutPageId)

  const {
    isLoading,
    errors: formStepErrors,
    submit,
  } = useBookingCalendarOptIn({
    optInButtonEntity: entity,
    closePopup,
    setActiveStep,
    surveysResults: {
      [entity.id]: {
        surveyEntityId: entity.id,
        questions: customQuestions,
      },
    },
  })

  const {
    errors: calendarStepErrors,
    isErrorsVisible,
    setIsErrorsVisible,
  } = useCalendarErrors()

  const { hasThankYouStepButton, redirectToUrl } =
    useBookingCalendarConfirmed(entity)

  return !event ? (
    <></>
  ) : (
    <StyleWrapper
      mobileMargin={entity.mobileMargin}
      margin={entity.margin}
      appearance={entity.appearance}
    >
      <CommonBookingCalendar
        ref={calendarRef}
        entity={entity}
        selectedDate={selectedDate}
        onDateSelect={date => {
          handleChangeField('date', date)
          handleChangeField('timeSlot', '')
        }}
        selectedTimeSlot={selectedTimeSlot}
        onTimeSlotSelect={timeSlot => handleChangeField('timeSlot', timeSlot)}
        selectedTimeZone={selectedTimeZone}
        onTimeZoneSelect={tz => {
          if (event?.displayTimezoneType !== EventTimeZoneTypeEnum.Fixed) {
            handleChangeField('timeZone', tz)
            handleChangeField('date', '')
            handleChangeField('timeSlot', '')
          }
        }}
        selectedLocationId={selectedLocationId}
        onLocationSelect={locationId => {
          handleChangeField('eventLocationId', locationId)
        }}
        onMonthSelect={onMonthSelect}
        onNextStepClick={e => {
          e?.preventDefault()
          if (!calendarStepErrors.length) {
            setActiveStep(BookingCalendarSteps.FormStep)
            scrollElementTopIntoViewIfTopIntoView(calendarRef.current)
          } else if (!isErrorsVisible) {
            setIsErrorsVisible(true)
          }
        }}
        onFormSubmit={submit}
        formStepErrors={formStepErrors}
        isFormSubmitDisabled={isLoading}
        calendarStepErrors={isErrorsVisible ? calendarStepErrors : []}
        onPrevStepClick={() => {
          setActiveStep(BookingCalendarSteps.CalendarStep)
        }}
        customQuestions={customQuestions}
        setCustomQuestions={setCustomQuestions}
        activeStep={activeStep}
        availabilitySlots={timeSlots}
        event={event}
        fontSize={globalFontSize}
        mobileFontSize={globalMobileFontSize}
        fontFamily={globalFontFamily}
        mobileFontFamily={globalFontFamily}
        isEventLoading={isEventFetching}
        formStepChildren={<ChildrenEntities childIds={entity.childIds} />}
        hasThankYouStepButton={hasThankYouStepButton}
        thankYouStepRedirectToUrl={redirectToUrl}
        isMobile={!isDesktop}
      />
    </StyleWrapper>
  )
}

export default BookingCalendar
